import { convertFirebaseTSToDate } from '../../../utils/helpers';
import type { TestRecord, Category } from '../../../data/interface-question';
import type {
	TrendMetrics,
	PerformanceStats,
	PerformanceVelocity,
	ChartDataPoint,
	Recommendation
} from '../types/analytics';

/**
 * Helper function to safely convert grade to number
 */
const gradeToNumber = (grade: string | number): number => {
	if (typeof grade === 'string') {
		return parseFloat(grade);
	}
	return grade;
};

/**
 * Helper function to validate if a grade is valid
 */
const isValidGrade = (grade: string | number): boolean => {
	const gradeAsNumber = gradeToNumber(grade);
	return gradeAsNumber != null &&
		!isNaN(gradeAsNumber) &&
		isFinite(gradeAsNumber) &&
		gradeAsNumber >= 0;
};

/**
 * Calculates performance velocity and improvement trend
 */
export const calculatePerformanceVelocity = (records: TestRecord[]): PerformanceVelocity | null => {
	if (records.length < 3) return null;

	// Filter out records with invalid grades and handle string/number conversion
	const validRecords = records.filter(r => isValidGrade(r.grade));

	if (validRecords.length < 3) return null;

	const sortedRecords = [...validRecords].sort((a, b) =>
		new Date(convertFirebaseTSToDate(a.date)).getTime() - new Date(convertFirebaseTSToDate(b.date)).getTime()
	);

	const recentCount = Math.min(3, Math.floor(sortedRecords.length / 2));
	const recent = sortedRecords.slice(-recentCount);
	const earlier = sortedRecords.slice(0, recentCount);

	const recentAvg = recent.reduce((sum, r) => {
		const gradeAsNumber = gradeToNumber(r.grade);
		return sum + gradeAsNumber;
	}, 0) / recent.length;
	const earlierAvg = earlier.reduce((sum, r) => {
		const gradeAsNumber = gradeToNumber(r.grade);
		return sum + gradeAsNumber;
	}, 0) / earlier.length;

	const velocity = recentAvg - earlierAvg;
	const isImproving = velocity > 0;

	let trendDirection: 'up' | 'down' | 'stable' = 'stable';
	if (Math.abs(velocity) > 5) {
		trendDirection = velocity > 0 ? 'up' : 'down';
	}

	return {
		velocity: Math.round(velocity * 100) / 100,
		isImproving,
		confidenceLevel: validRecords.length >= 10 ? 'high' : validRecords.length >= 6 ? 'medium' : 'low',
		trendDirection
	};
};

/**
 * Calculates comprehensive trend metrics
 */
export const calculateTrendMetrics = (records: TestRecord[]): TrendMetrics => {
	if (records.length === 0) {
		return {
			improvement: 0,
			trend: 'stable',
			velocity: 0,
			bestStreak: 0,
			confidenceLevel: 'low'
		};
	}

	// Filter out records with invalid grades and handle string/number conversion
	const validRecords = records.filter(r => isValidGrade(r.grade));

	if (validRecords.length === 0) {
		return {
			improvement: 0,
			trend: 'stable',
			velocity: 0,
			bestStreak: 0,
			confidenceLevel: 'low'
		};
	}

	const sortedRecords = [...validRecords].sort((a, b) =>
		new Date(convertFirebaseTSToDate(a.date)).getTime() - new Date(convertFirebaseTSToDate(b.date)).getTime()
	);

	// Calculate improvement from first to last
	const firstScore = gradeToNumber(sortedRecords[0].grade);
	const lastScore = gradeToNumber(sortedRecords[sortedRecords.length - 1].grade);
	const improvement = lastScore - firstScore;

	// Calculate velocity
	const velocityData = calculatePerformanceVelocity(validRecords);
	const velocity = velocityData?.velocity ?? 0;

	// Determine trend
	let trend: 'improving' | 'declining' | 'stable' = 'stable';
	if (Math.abs(improvement) > 5) {
		trend = improvement > 0 ? 'improving' : 'declining';
	}

	// Calculate best streak
	let currentStreak = 0;
	let bestStreak = 0;
	for (let i = 1; i < sortedRecords.length; i++) {
		const currentGrade = gradeToNumber(sortedRecords[i].grade);
		const previousGrade = gradeToNumber(sortedRecords[i - 1].grade);
		if (currentGrade >= previousGrade) {
			currentStreak++;
			bestStreak = Math.max(bestStreak, currentStreak);
		} else {
			currentStreak = 0;
		}
	}

	return {
		improvement: Math.round(improvement * 100) / 100,
		trend,
		velocity,
		bestStreak,
		confidenceLevel: validRecords.length >= 10 ? 'high' : validRecords.length >= 5 ? 'medium' : 'low'
	};
};

/**
 * Calculates performance statistics summary
 */
export const calculatePerformanceStats = (records: TestRecord[]): PerformanceStats => {
	if (records.length === 0) {
		return {
			totalTests: 0,
			averageScore: 0,
			bestScore: 0,
			totalStudyTime: 0,
			recentPerformance: 0
		};
	}

	// Filter out records with invalid grades and ensure we have valid grade values
	// Handle both string and number grades from database inconsistencies
	const validRecords = records.filter(r => isValidGrade(r.grade));

	if (validRecords.length === 0) {
		return {
			totalTests: records.length,
			averageScore: 0,
			bestScore: 0,
			totalStudyTime: records.reduce((sum, r) => sum + (r.timeElapsedInSeconds ?? 0), 0),
			recentPerformance: 0
		};
	}

	const totalTests = records.length;

	// Convert grades to numbers for calculations
	const validGrades = validRecords.map(r => gradeToNumber(r.grade));
	const averageScore = validGrades.reduce((sum, grade) => sum + grade, 0) / validGrades.length;
	const bestScore = Math.max(...validGrades);
	const totalStudyTime = records.reduce((sum, r) => sum + (r.timeElapsedInSeconds ?? 0), 0);

	// Recent performance (last 3 tests average) - filter valid records first
	const recentTests = records.slice(-3).filter(r => isValidGrade(r.grade));

	const recentPerformance = recentTests.length > 0
		? recentTests.reduce((sum, r) => {
			const gradeAsNumber = gradeToNumber(r.grade);
			return sum + gradeAsNumber;
		}, 0) / recentTests.length
		: 0;

	return {
		totalTests,
		averageScore: Math.round(averageScore * 100) / 100,
		bestScore,
		totalStudyTime,
		recentPerformance: Math.round(recentPerformance * 100) / 100
	};
};

/**
 * Generates smart recommendations based on performance data
 */
export const generateSmartRecommendations = (
	categories: Category[],
	testRecords: TestRecord[]
): Recommendation[] => {
	const recommendations: Recommendation[] = [];
	const processedCategories = new Set<string>(); // Track categories to avoid duplicates

	if (categories.length === 0 || testRecords.length === 0) {
		return recommendations;
	}

	// Find categories with performance data
	const categoriesWithPerformance = categories
		.filter(cat => (cat.totalAnsweredInQuestionRecords ?? 0) > 0)
		.map(cat => ({
			...cat,
			percentage: ((cat.totalAnsweredCorrectly ?? 0) / (cat.totalAnsweredInQuestionRecords ?? 1)) * 100
		}))
		.sort((a, b) => a.percentage - b.percentage);

	if (categoriesWithPerformance.length > 0) {
		// Find weakest category (only if we have multiple categories or single category performance is poor)
		const weakest = categoriesWithPerformance[0];
		const shouldRecommendWeakest = categoriesWithPerformance.length > 1 || weakest.percentage < 60;

		if (shouldRecommendWeakest && weakest.percentage < 60) {
			recommendations.push({
				type: 'weakness',
				category: weakest.name,
				message: `Tu rendimiento en ${weakest.name} necesita atención (${Math.round(weakest.percentage)}%)`,
				action: `Dedica más tiempo a estudiar ${weakest.name}`,
				priority: 'high',
				icon: '⚠️'
			});
			processedCategories.add(weakest.name);
		}

		// Find strongest category (only if different from weakest and performance is good)
		const strongest = categoriesWithPerformance[categoriesWithPerformance.length - 1];
		const shouldRecommendStrongest =
			!processedCategories.has(strongest.name) &&
			strongest.percentage >= 80 &&
			categoriesWithPerformance.length > 1; // Only recommend strength if we have multiple categories

		if (shouldRecommendStrongest) {
			recommendations.push({
				type: 'strength',
				category: strongest.name,
				message: `¡Excelente dominio en ${strongest.name}! (${Math.round(strongest.percentage)}%)`,
				action: `Mantén tu nivel en ${strongest.name} con revisiones periódicas`,
				priority: 'low',
				icon: '🎯'
			});
			processedCategories.add(strongest.name);
		}
	}

	// Check for improvement trends
	if (testRecords.length >= 5) {
		const velocityData = calculatePerformanceVelocity(testRecords);

		if ((velocityData?.isImproving === true) && velocityData.velocity > 5) {
			recommendations.push({
				type: 'strength',
				message: `¡Excelente progreso! Has mejorado ${Math.round(velocityData.velocity)} puntos promedio`,
				action: 'Mantén tu ritmo de estudio actual',
				priority: 'medium',
				icon: '📈'
			});
		} else if (velocityData != null && !velocityData.isImproving && velocityData.velocity < -5) {
			recommendations.push({
				type: 'weakness',
				message: `Tu rendimiento ha disminuido ${Math.abs(Math.round(velocityData.velocity))} puntos promedio`,
				action: 'Considera revisar tu estrategia de estudio',
				priority: 'high',
				icon: '📉'
			});
		}
	}

	// Study consistency recommendations
	if (testRecords.length >= 3) {
		const sortedRecords = [...testRecords].sort((a, b) =>
			new Date(convertFirebaseTSToDate(b.date)).getTime() - new Date(convertFirebaseTSToDate(a.date)).getTime()
		);

		const daysSinceLastTest = Math.floor(
			(new Date().getTime() - new Date(convertFirebaseTSToDate(sortedRecords[0].date)).getTime()) / (1000 * 60 * 60 * 24)
		);

		if (daysSinceLastTest > 7) {
			recommendations.push({
				type: 'opportunity',
				message: `Han pasado ${daysSinceLastTest} días desde tu último examen`,
				action: 'Considera hacer un examen de práctica para mantener el ritmo',
				priority: 'medium',
				icon: '📅'
			});
		}
	}

	return recommendations.slice(0, 4); // Limit to top 4 recommendations
};

/**
 * Enhances chart data with additional calculated fields
 */
export const enhanceChartData = (records: TestRecord[]): ChartDataPoint[] => {
	return records
		.filter(record => isValidGrade(record.grade) && record.testLength > 0)
		.map(record => {
			const safeGrade = gradeToNumber(record.grade) ?? 0;
			const safeTestLength = record.testLength ?? 1;
			const safeCorrectAnswers = record.correctAnswers ?? 0;

			// Calculate percentage using correct answers vs total questions, not grade vs test length
			const calculatedPercentage = safeTestLength > 0
				? Math.round((safeCorrectAnswers / safeTestLength) * 100)
				: 0;

			return {
				correctas: safeCorrectAnswers,
				name: new Date(convertFirebaseTSToDate(record.date)).toLocaleDateString('es-ES', {
					month: 'short',
					day: 'numeric'
				}),
				nota: safeGrade,
				preguntas: safeTestLength,
				percentage: Math.min(calculatedPercentage, 100), // Cap at 100% to prevent invalid percentages
				date: new Date(convertFirebaseTSToDate(record.date))
			};
		});
};

/**
 * Calculates performance level for a category
 */
export const calculatePerformanceLevel = (percentage: number): 'excellent' | 'good' | 'needs-improvement' | 'critical' => {
	if (percentage >= 85) return 'excellent';
	if (percentage >= 70) return 'good';
	if (percentage >= 50) return 'needs-improvement';
	return 'critical';
};

/**
 * Formats time duration from seconds to readable format
 */
export const formatStudyTime = (seconds: number): string => {
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);

	if (hours > 0) {
		return `${hours}h ${minutes}m`;
	}
	return `${minutes}m`;
};
